'use client';

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Typography } from '../../../components';
import { useTranslations } from '../../../contexts/translation.context';
import { usePageBreadcrumbs } from '../../../hooks/usePageBreadcrumbs';
import { FiltersSection, SubscribersTable } from './components';
import { SubscribersFilters, RevshareSubscriber, AvailableModel, AvailableCampaign } from './types';
import { DEFAULT_FILTERS, DEFAULT_PAGE_SIZE, SEARCH_DEBOUNCE_DELAY } from './constants';
import partnerRevshareService from '../../../api/partner/partnerRevshareService';

const RevshareSubscribers: React.FC = () => {
  const { t } = useTranslations();

  // Breadcrumbs
  const breadcrumbs = useMemo(
    () => [
      { label: t('sidebar.dashboard') || 'Dashboard', href: '/partner/dashboard' },
      { label: 'Revshare', href: '/partner/revshare/offers' },
      { label: t('partner.revshare.subscribers.title') || 'Subscribers', isActive: true },
    ],
    [t]
  );
  usePageBreadcrumbs(breadcrumbs);

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [subscribers, setSubscribers] = useState<RevshareSubscriber[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [availableCampaigns, setAvailableCampaigns] = useState<AvailableCampaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [totalIncome, setTotalIncome] = useState(0);
  // Filters
  const [filters, setFilters] = useState<SubscribersFilters>(DEFAULT_FILTERS);
  const [localSearch, setLocalSearch] = useState('');

  // Calculate total income from subscribers

  // Load available models from API
  const fetchAvailableModels = useCallback(async (search: string = '') => {
    setModelsLoading(true);
    try {
      const response = await partnerRevshareService.getCompaniesModels({
        page: 1,
        page_size: 50,
        search: search || undefined,
      });
      if (response.objects) {
        setAvailableModels(
          response.objects.map((acc) => ({
            id: acc._id,
            name: `${String(acc.of_user_id).slice(-4)}`,
            username: acc.of_user_name || '',
            avatar: acc.avatar || '',
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    } finally {
      setModelsLoading(false);
    }
  }, []);

  // Fetch campaigns from orders
  const fetchCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const ordersResponse = await partnerRevshareService.getOrders({ page: 1, page_size: 50 });

      // Extract campaigns from orders with full data
      const campaignsFromOrders: AvailableCampaign[] = ordersResponse.objects.map((order) => ({
        id: order._id,
        shortId: order._id,
        modelName: order.model?.of_user_name || order.model?.name || '',
        modelAvatar: order.model?.avatar,
        source: order.source || 'only_fans',
        createdAt: order.created_at,
        type:
          order.type === 'FREE' || order.type === 'FREE_TRIAL'
            ? ('ref_share' as const)
            : ('cpl' as const),
        model: order.model,
      }));

      setAvailableCampaigns(campaignsFromOrders);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setCampaignsLoading(false);
    }
  }, []);

  // Initial load of models and campaigns - only once on mount
  useEffect(() => {
    fetchAvailableModels();
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== '' ||
      filters.account !== '' ||
      filters.campaign !== '' ||
      filters.campaignId !== '' ||
      filters.period !== '' ||
      filters.paidSubscription ||
      filters.paidSubscriptionAutoRenew ||
      filters.onePurchase ||
      filters.twoPurchases ||
      filters.fivePurchases ||
      filters.payments500 ||
      filters.messages
    );
  }, [filters]);

  // Ref to track current request ID (to handle StrictMode double-invocation)
  const requestIdRef = useRef(0);
  // Ref to skip debounce on initial mount
  const isInitialMount = useRef(true);

  // Fetch subscribers from API - directly in useEffect to avoid double render
  useEffect(() => {
    // Increment request ID to invalidate any in-flight requests
    const currentRequestId = ++requestIdRef.current;

    setIsLoading(true);

    const fetchData = async () => {
      try {
        const params: any = {
          page: currentPage,
          page_size: pageSize,
          sort_by: 'created_at', // Default: newest first
        };

        // Search by name or id
        if (filters.search) {
          // If search looks like an ID (numeric), search by id, otherwise by name
          if (/^\d+$/.test(filters.search)) {
            params.id = filters.search;
          } else {
            params.name = filters.search;
          }
        }

        // Model/Account filter
        if (filters.account) {
          params.model_id = filters.account;
        }

        // Campaign type filter (cpl/refshare)
        if (filters.campaign) {
          params.compaign_type = filters.campaign;
        }

        // Specific campaign filter
        if (filters.campaignId) {
          params.company_id = filters.campaignId;
        }

        // Checkbox filters - map to API params
        if (filters.messages) {
          params.has_gte_three_messages = true;
        }
        if (filters.onePurchase) {
          params.one_transaction = true;
        }
        if (filters.twoPurchases) {
          params.two_transaction = true;
        }
        if (filters.fivePurchases) {
          params.five_transaction = true;
        }
        if (filters.payments500) {
          params.amount_more_500 = true;
        }

        const response = await partnerRevshareService.getSubscribers(params);

        // Only update state if this is still the current request
        if (requestIdRef.current !== currentRequestId) {
          return;
        }
        setTotalIncome(response.total_amount || 0);
        setSubscribers(response.objects);
        setTotalCount(response.count);
      } catch (error: any) {
        // Only handle error if this is still the current request
        if (requestIdRef.current !== currentRequestId) {
          return;
        }
        console.error('Error fetching subscribers:', error);
        setSubscribers([]);
        setTotalCount(0);
      } finally {
        // Only update loading state if this is still the current request
        if (requestIdRef.current === currentRequestId) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
  }, [currentPage, pageSize, filters]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Debounce search - only update if value actually changed, skip on initial mount
  useEffect(() => {
    // Skip debounce on initial mount to prevent double render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const timeoutId = setTimeout(() => {
      setFilters((prev) => {
        if (prev.search !== localSearch) {
          return { ...prev, search: localSearch };
        }
        return prev;
      });
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_DELAY);

    return () => clearTimeout(timeoutId);
  }, [localSearch]);

  const handleFilterChange = useCallback(
    (key: keyof SubscribersFilters, value: string | boolean) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setCurrentPage(1);
    },
    []
  );

  const handleResetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setLocalSearch('');
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleModelSearchChange = useCallback(
    (search: string) => {
      fetchAvailableModels(search);
    },
    [fetchAvailableModels]
  );

  return (
    <div className="w-full px-4 py-6 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <Typography variant="h4" className="mb-2">
          {t('partner.revshare.subscribers.title')}
        </Typography>
      </div>

      {/* Filters */}
      <FiltersSection
        filters={filters}
        localSearch={localSearch}
        availableModels={availableModels}
        availableCampaigns={availableCampaigns}
        isLoading={modelsLoading}
        isCampaignsLoading={campaignsLoading}
        hasActiveFilters={hasActiveFilters}
        onSearchChange={setLocalSearch}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
        onModelSearchChange={handleModelSearchChange}
      />

      {/* Table */}
      <SubscribersTable
        subscribers={subscribers}
        isLoading={isLoading}
        totalCount={totalCount}
        totalIncome={totalIncome}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};

export default RevshareSubscribers;
